import React, { useState, useEffect } from 'react';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';
import { jwtDecode } from 'jwt-decode';
import { Button } from 'react-bootstrap';
import { useLoading } from '../../context/LoadingContext';
import config from '../../aws-exports';
import '../../styles/general.css';
import '../../styles/my-clients.css';

const MyClientsDashboard = () => {
  const [activeTab, setActiveTab] = useState('clientDetails');
  const [clients, setClients] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedClient, setSelectedClient] = useState(null);
  const [names, setNames] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [zipcodes, setZipcodes] = useState([]);
  const [filterTerms, setFilterTerms] = useState({ name: '', company: '', pin: '' });
  const [clientChanges, setClientChanges] = useState({});
  const [isSaveEnabled, setIsSaveEnabled] = useState(false);
  const navigate = useNavigate();
  const pageSize = 10;
  const { setLoading } = useLoading();

  const getBackendName = () => {
    const backend = config.aws_user_files_s3_bucket.split('-');
    return backend[backend.length - 1];
  };
  
  const fetchSearchParams = async (option, suboption) => {
    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
  
      const user = await Auth.currentAuthenticatedUser();
      const token = user.signInUserSession.idToken.jwtToken;
      const backendName = getBackendName();

      const updatedFilterTerms = {
        ...filterTerms,
        GPEmailID: user.attributes.email
      };
  
      const command = new InvokeCommand({
        FunctionName: `dataops-${backendName}`,
        Payload: JSON.stringify({
          body: {
            target: 'listDynamicDropDownOptions',
            userTable: 'AMTPLUsers',
            option: option,
            suboption: suboption,
            filterTerms: updatedFilterTerms,
            access_token: token,
            rowStart: 0,
            pageSize: 100 
          }
        })
      });
  
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
      if (result.statusCode === 200) {
        result = JSON.parse(result.body);
        if(suboption === 'names') {
          setNames(result.items.map(option => option.result));
        } else if(suboption === 'companies') {
          setCompanies(result.items.map(option => option.result));
        } else if(suboption === 'zipcodes') {
          setZipcodes(result.items.map(option => option.result));
        }
      } else {
        console.error('Error fetching products: ', result);
      }
    } catch (error) {
      console.error('Error fetching products', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        // Check if the user is a member of the AMTPL_GLOBAL_PARTNERS group
        const groups = decodedToken['cognito:groups'] || [];
        if (!groups.includes('AMTPL_GLOBAL_PARTNERS')) {
          await Auth.signOut(); // Sign out the user
          navigate('/login/global-partner');
        }
      } catch (error) {
        await Auth.signOut(); // Sign out the user
        navigate('/login/global-partner');
      }
    };

    const fetchClients = async () => {
      setLoading(true);
      try {
        const credentials = await Auth.currentCredentials();
        const lambdaClient = new LambdaClient({
          credentials: Auth.essentialCredentials(credentials),
          region: config.aws_project_region
        });
        const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
        const updatedFilterTerms = {
          ...filterTerms,
          GPEmailID: user.attributes.email
        };

        const body = {
          target: 'listAMTPLUsers',
          access_token: user.signInUserSession.idToken.jwtToken,
          filterTerms: updatedFilterTerms,
          rowStart: (currentPage - 1) * pageSize,
          pageSize: pageSize
        };

        const params = {
          FunctionName: `dataops-${getBackendName()}`,
          Payload: JSON.stringify({ body })
        };

        const command = new InvokeCommand(params);
        const data = await lambdaClient.send(command);
        let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
        if (result.statusCode === 200) {
          result = JSON.parse(result.body);
          setClients(result.items);
          setTotalPages(Math.ceil(result.total / pageSize));
          if (result.items.length > 0) {
            setSelectedClient(result.items[0]);
            setClientChanges(result.items[0]);
          }
        } else {
          console.error('Error fetching clients: ', result);
        }
      } catch (error) {
        console.error('Error invoking Lambda function: ', error);
      } finally {
        setLoading(false);
      }
    };

    checkUser();
    fetchClients();
    fetchSearchParams('globalpartner', 'names');
    fetchSearchParams('globalpartner', 'companies');
    fetchSearchParams('globalpartner', 'zipcodes');
  }, [navigate, currentPage, setLoading, filterTerms]);

  const handleSignOut = async () => {
    setLoading(true);
    try {
      await Auth.signOut();
      navigate('/login/global-partner');
    } catch (error) {
      console.error('Error signing out: ', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleRowClick = (client) => {
    setSelectedClient(client);
    setClientChanges(client);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterTerms((prevTerms) => ({ ...prevTerms, [name]: value }));
  };

  const handleSearch = () => {
    setCurrentPage(1);
  };

  const handleReset = () => {
    setFilterTerms({ name: '', company: '', pin: '' });
    resetFilter();
  };

  const resetFilter = () => {
    setSelectedClient(null);
    setCurrentPage(1);
    setClientChanges({});
    setIsSaveEnabled(false);    
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setClientChanges((prevChanges) => ({ ...prevChanges, [name]: value }));
    setIsSaveEnabled(true);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const credentials = await Auth.currentCredentials();
      const lambdaClient = new LambdaClient({
        credentials: Auth.essentialCredentials(credentials),
        region: config.aws_project_region
      });
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });
  
      const body = {
        target: 'updateAMTPLUser',
        access_token: user.signInUserSession.idToken.jwtToken,
        updateAMTPLUserInput: clientChanges
      };
  
      const params = {
        FunctionName: `dataops-${getBackendName()}`,
        Payload: JSON.stringify({ body })
      };
  
      const command = new InvokeCommand(params);
      const data = await lambdaClient.send(command);
      let result = JSON.parse(new TextDecoder('utf-8').decode(data.Payload));
      if (result.statusCode === 200) {
        // Use clientChanges directly to update the clients state
        setClients((prevClients) =>
          prevClients.map((client) =>
            client.AMTPLUserID === clientChanges.AMTPLUserID ? clientChanges : client
          )
        );
        setSelectedClient(clientChanges);
        setClientChanges(clientChanges);
        setIsSaveEnabled(false);
      } else {
        console.error('Error updating client: ', result);
      }
    } catch (error) {
      console.error('Error invoking Lambda function: ', error);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="my-clients-dashboard">
      <header>
        <img src="/images/logo.png" alt="Grains Global Logo" className="logo" />
        <nav>
          <ul>
            <li><a href="/global-partner">Dashboard</a></li>
            
            <li><button onClick={handleSignOut} className="sign-out-button">Sign Out</button></li>
          </ul>
        </nav>
      </header>

      <div className="content">
        <main className="main-content">
          <div className='offers-search row'>
            <div className='col-lg-3 col-sm-4 col-6'>
              <select className="form-control form-select" id="name" name="name" value={filterTerms.name || ''} onChange={handleFilterChange}>
                <option value="">Select Name</option>
                {names.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div className='col-lg-3 col-sm-4 col-6'>
              <select className="form-control form-select" id="company" name="company" value={filterTerms.company || ''} onChange={handleFilterChange}>
                <option value="">Select Company</option>
                {companies.map((company) => (
                  <option key={company} value={company}>{company}</option>
                ))}
              </select>
            </div>
            <div className='col-lg-3 col-sm-4 col-6'>
              <select className="form-control form-select" id="pin" name="pin" value={filterTerms.pin || ''} onChange={handleFilterChange}>
                <option value="">Select PIN/ZIP</option>
                {zipcodes.map((pin) => (
                  <option key={pin} value={pin}>{pin}</option>
                ))}
              </select>
            </div>
            <div className='col-lg-3 col-sm-4 col-6'>
              <button onClick={handleSearch} className='mx-3 d-inline'>Search</button>
              <button onClick={handleReset} className='d-inline'>Reset</button>
            </div>
          </div>
          {/* <div className="clients-search">
            <input
              type="text"
              name="name"
              placeholder="Name"
              className="form-control"
              value={filterTerms.name}
              onChange={handleFilterChange}
            />
            <input
              type="text"
              name="company"
              placeholder="Company"
              className="form-control"
              value={filterTerms.company}
              onChange={handleFilterChange}
            />
            <input
              type="text"
              name="pin"
              placeholder="PIN/ZIP"
              className="form-control"
              value={filterTerms.pin}
              onChange={handleFilterChange}
            />
            <div className="button-container">
              <Button variant="primary" onClick={handleSearch}>Search</Button>
              <Button variant="primary" onClick={handleReset}>Reset</Button>
            </div>
          </div> */}

          <section className="clients">
            <table>
              <thead>
                <tr>
                  <th>Client Id</th>
                  <th>Client Name</th>
                  <th>Company</th>
                  <th>Country</th>
                  <th>State</th>
                  <th>Phone</th>
                  <th>Email</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((client) => (
                  <tr
                    key={client.AMTPLUserID}
                    onClick={() => handleRowClick(client)}
                    className={selectedClient && selectedClient.AMTPLUserID === client.AMTPLUserID ? 'selected' : ''}
                  >
                    <td>{client.AMTPLUserID}</td>
                    <td>{client.FirstName} {client.LastName}</td>
                    <td>{client.CompanyName}</td>
                    <td>{client.Country}</td>
                    <td>{client.State}</td>
                    <td>{client.Phone1}</td>
                    <td>{client.CompanyEmail}</td>
                  </tr>
                ))}
                {clients.length === 0 && (
                  <tr>
                    <td colSpan="7">No clients available</td>
                  </tr>
                )}
              </tbody>
            </table>
            <div className="pagination">
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>{'<'}</button>
              {Array.from({ length: totalPages }, (_, index) => (
                <button
                  key={index + 1}
                  onClick={() => handlePageChange(index + 1)}
                  className={currentPage === index + 1 ? 'active' : ''}
                >
                  {index + 1}
                </button>
              ))}
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>{'>'}</button>
            </div>
          </section>

          <section className="client-details">
            <div className="tabs">
              <button
                className={activeTab === 'clientDetails' ? 'active' : ''}
                onClick={() => handleTabClick('clientDetails')}
              >
                Client Details
              </button>
              <button
                className={activeTab === 'companyDetails' ? 'active' : ''}
                onClick={() => handleTabClick('companyDetails')}
              >
                Company Details
              </button>
            </div>

            {selectedClient && activeTab === 'clientDetails' && (
              <div className="tab-content active">
                <div className='row'>
                  <div className='col-6'>
                    <label>First Name</label>
                    <input type="text" name="FirstName" value={clientChanges.FirstName} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Last Name</label>
                    <input type="text" name="LastName" value={clientChanges.LastName} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-3'>
                    <label>Age</label>
                    <input type="text" name="Age" value={clientChanges.Age} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-3'>
                    <label>Gender</label>
                    <input type="text" name="Gender" value={clientChanges.Gender} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-3'>
                    <label>Phone 1</label>
                    <input type="text" name="Phone1" value={clientChanges.Phone1} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-3'>
                    <label>Phone 2</label>
                    <input type="text" name="Phone2" value={clientChanges.Phone2} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Email</label>
                    <input type="text" name="BuyerEmailID" value={clientChanges.BuyerEmailID} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>LinkedIn</label>
                    <input type="text" name="LinkedIn" value={clientChanges.LinkedIn} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Twitter</label>
                    <input type="text" name="Twitter" value={clientChanges.Twitter} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Website</label>
                    <input type="text" name="Website" value={clientChanges.Website} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Address 1</label>
                    <input type="text" name="Address1" value={clientChanges.Address1} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Address 2</label>
                    <input type="text" name="Address2" value={clientChanges.Address2} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Address 3</label>
                    <input type="text" name="Address3" value={clientChanges.Address3} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>PIN/ZIP</label>
                    <input type="text" name="ZipCode" value={clientChanges.ZipCode} onChange={handleInputChange} className='form-control' />
                  </div>
                </div>
                </div>
            )}

            {selectedClient && activeTab === 'companyDetails' && (
              <div className="tab-content active">
                <div className='row'>
                  <div className='col-6'>
                    <label>Company Name</label>
                    <input type="text" name="CompanyName" value={clientChanges.CompanyName} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Website</label>
                    <input type="text" name="CompanyWebsite" value={clientChanges.CompanyWebsite} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Email</label>
                    <input type="text" name="CompanyEmail" value={clientChanges.CompanyEmail} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Address 1</label>
                    <input type="text" name="CompanyAddress1" value={clientChanges.CompanyAddress1} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Address 2</label>
                    <input type="text" name="CompanyAddress2" value={clientChanges.CompanyAddress2} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Address 3</label>
                    <input type="text" name="CompanyAddress3" value={clientChanges.CompanyAddress3} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>PIN/ZIP</label>
                    <input type="text" name="CompanyZipCode" value={clientChanges.CompanyZipCode} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>LinkedIn</label>
                    <input type="text" name="CompanyLinkedIn" value={clientChanges.CompanyLinkedIn} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Twitter</label>
                    <input type="text" name="CompanyTwitter" value={clientChanges.CompanyTwitter} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Phone 1</label>
                    <input type="text" name="Phone1" value={clientChanges.Phone1} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Phone 2</label>
                    <input type="text" name="Phone2" value={clientChanges.Phone2} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Phone 3</label>
                    <input type="text" name="Phone3" value={clientChanges.Phone3} onChange={handleInputChange} className='form-control' />
                  </div>
                  <div className='col-6'>
                    <label>Phone 4</label>
                    <input type="text" name="Phone4" value={clientChanges.Phone4} onChange={handleInputChange} className='form-control' />
                  </div>
                </div>
              </div>
            )}

            <div className="actions mt-2 pb-3">
              <button onClick={handleSave} disabled={!isSaveEnabled}>Save</button>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default MyClientsDashboard;
